import { useCallback, useMemo, useState } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { PersonaRuleOperator } from "@/api_framework/api_modals/HomeTileConfig";
import SimpleCheckbox from "@/app/components/ui/CheckBoxes/SimpleCheckbox";
import RadioGroupComponent from "@/app/components/ui/RadioGroup";
import MultiSelectDropdown from "@/app/components/ui/SelectMenus/MultiSelectDropdown/index";
import { PersonaExpressionText, RibbonPreview, StatusBadge } from "../components";
import { SegmentedControl } from "../controls";
import {
  DEFAULT_BASIC_PERSONA_LOGIC,
  IBasicPersonaLogic,
  IPersonaCombinationSummary,
  IPersonaLogicFormFields,
  PERSONA_LOGIC_MODES,
  PERSONA_RULE_OPERATORS,
  PersonaLogicMode,
  sectionLabelClassName,
} from "../interface";
import { ALL_INCLUSIVE_PERSONA, fromPersonaRule, validatePersonaCombination } from "../personaExpression";
import { toBasicExpression, toBasicPersonaLogic } from "./basicPersonaLogic";
import { PersonaCombinationField } from "./PersonaCombinationField";

const toDropdownItems = (personas: string[]) => personas.map((title, index) => ({ id: index + 1, title }));

const toRadioOptions = <TKey extends string>(options: { key: TKey; label: string }[]) =>
  options.map(({ key, label }) => ({ id: key, title: label }));

const OPERATOR_RADIO_OPTIONS = toRadioOptions(PERSONA_RULE_OPERATORS);

const detachedRegister = () => ({});

const isSameList = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((item, index) => item === right[index]);

const SelectedPersonaGrid = ({ personas, emptyText }: { personas: string[]; emptyText: string }) => (
  <div className="rounded-lg border border-gray-200 bg-white">
    <div className="flex items-center justify-between border-b border-gray-100 px-3 py-1.5">
      <span className={sectionLabelClassName}>Selected</span>
      <span className="text-[11px] font-semibold text-gray-500">{personas.length}</span>
    </div>
    {personas.length ? (
      <ul className="grid max-h-[180px] grid-cols-2 gap-1.5 overflow-y-auto p-2 sm:grid-cols-3">
        {personas.map((persona) => (
          <li
            key={persona}
            title={persona}
            className="flex h-7 min-w-0 items-center rounded-md border border-[#193A6A]/20 bg-[#EEF5FF] px-2 text-[11px] font-medium text-[#193A6A]"
          >
            <span className="truncate">{persona}</span>
          </li>
        ))}
      </ul>
    ) : (
      <p className="px-3 py-3 text-center text-[11px] text-gray-400">{emptyText}</p>
    )}
  </div>
);

const BasicPersonaLogic = ({ personas: allPersonas }: { personas: string[] }) => {
  const { control, getValues, register, setValue } = useFormContext<IPersonaLogicFormFields>();
  const personas = useMemo(() => allPersonas.filter((persona) => persona !== ALL_INCLUSIVE_PERSONA), [allPersonas]);
  const logic = useWatch({ control, name: "basic_persona" }) ?? DEFAULT_BASIC_PERSONA_LOGIC;

  const update = useCallback(
    (changes: Partial<IBasicPersonaLogic>) => {
      const current = getValues("basic_persona") ?? DEFAULT_BASIC_PERSONA_LOGIC;
      const next = { ...current, ...changes };
      next.exclude = next.exclude.filter((persona) => !next.include.includes(persona));
      setValue("basic_persona", next, { shouldDirty: true });
      setValue("persona_combination", toBasicExpression(next), { shouldValidate: true, shouldDirty: true });
    },
    [getValues, setValue],
  );

  const selectIncluded = useCallback(
    (items: string[]) => {
      if (!isSameList(items, getValues("basic_persona.include") ?? [])) update({ include: items });
    },
    [getValues, update],
  );

  const selectExcluded = useCallback(
    (items: string[]) => {
      if (!isSameList(items, getValues("basic_persona.exclude") ?? [])) update({ exclude: items });
    },
    [getValues, update],
  );

  const includeItems = useMemo(() => toDropdownItems(personas), [personas]);
  const excludeItems = useMemo(
    () => toDropdownItems(personas.filter((persona) => !logic.include.includes(persona))),
    [personas, logic.include],
  );

  const includesEveryPersona = personas.length > 0 && logic.include.length === personas.length;

  return (
    <div>
      <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-800">User Persona</h3>
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 p-4">
          <p className="text-xs font-semibold text-gray-700">Include Personas</p>
          <div className="mt-3 flex flex-col gap-4 rounded-lg border border-gray-100 bg-gray-50/50 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-semibold text-gray-700">Include Using:</span>
              <RadioGroupComponent
                alignment="horizontal"
                errorMessages={[]}
                fieldError={undefined}
                register={register("basic_persona.include_operator")}
                groupOptions={OPERATOR_RADIO_OPTIONS}
                defaultSelectedId={logic.include_operator}
                onRadioChange={(optionId) => update({ include_operator: optionId as PersonaRuleOperator })}
                radioOptionClassName="text-sm"
              />
            </div>
            <div className="w-full">
              <MultiSelectDropdown
                data={includeItems}
                selectedKey="title"
                onSelectItem={selectIncluded}
                defaultSelected={logic.include}
                register={detachedRegister}
                name="basic_persona_include"
                showSelectAll={false}
                containerClassName="w-full"
              />
            </div>
            <SelectedPersonaGrid personas={logic.include} emptyText="No personas included yet." />
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 p-4">
          <SimpleCheckbox
            id="basic-persona-exclude"
            checked={logic.is_exclude_enabled}
            onChange={() => update({ is_exclude_enabled: !logic.is_exclude_enabled })}
            colorClassName="text-[#202223] cursor-pointer"
            disabled={includesEveryPersona}
            descriptionContent={
              <label
                htmlFor="basic-persona-exclude"
                className="ml-3 block text-xs font-semibold leading-6 text-gray-700"
              >
                Exclude Personas
              </label>
            }
          />
          {logic.is_exclude_enabled && (
            <div className="mt-3 flex flex-col gap-4 rounded-lg border border-gray-100 bg-gray-50/50 p-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs font-semibold text-gray-700">Excluded Using:</span>
                <RadioGroupComponent
                  alignment="horizontal"
                  errorMessages={[]}
                  fieldError={undefined}
                  register={register("basic_persona.exclude_operator")}
                  groupOptions={OPERATOR_RADIO_OPTIONS}
                  defaultSelectedId={logic.exclude_operator}
                  onRadioChange={(optionId) => update({ exclude_operator: optionId as PersonaRuleOperator })}
                  radioOptionClassName="text-sm"
                />
              </div>
              <div className="w-full">
                <MultiSelectDropdown
                  key={logic.include.join("|")}
                  data={excludeItems}
                  selectedKey="title"
                  onSelectItem={selectExcluded}
                  defaultSelected={logic.exclude}
                  register={detachedRegister}
                  name="basic_persona_exclude"
                  showSelectAll={false}
                  containerClassName="w-full"
                />
              </div>
              <SelectedPersonaGrid personas={logic.exclude} emptyText="No personas excluded yet." />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CurrentCombinations = ({ configs }: { configs: IPersonaCombinationSummary[] }) => (
  <div className="mt-4 rounded-lg border border-slate-200">
    <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/70 px-3 py-2">
      <span className={sectionLabelClassName}>Current persona combinations on this tile</span>
      <span className="text-[11px] text-slate-500">{configs.length}</span>
    </div>
    {configs.length ? (
      <ul className="max-h-60 divide-y divide-slate-100 overflow-y-auto">
        {configs.map((config) => (
          <li key={config.id} className="flex flex-wrap items-center gap-3 px-3 py-2">
            <div className="min-w-0 flex-1">
              {config.persona_rule ? (
                <PersonaExpressionText value={fromPersonaRule(config.persona_rule)} />
              ) : (
                <span className="text-xs text-slate-400">Dynamic only</span>
              )}
            </div>
            {config.text && (
              <RibbonPreview text={config.text} style={config.ribbon} isActive={config.status === "ACTIVE"} />
            )}
            <StatusBadge status={config.status} />
          </li>
        ))}
      </ul>
    ) : (
      <p className="px-3 py-4 text-center text-[11px] text-slate-400">No other rules on this tile yet.</p>
    )}
  </div>
);

export const PersonaLogicField = ({
  personas,
  isPersonaListLoading,
  validate,
  existingConfigs,
}: {
  personas: string[];
  isPersonaListLoading: boolean;
  validate: (value: string) => true | string;
  existingConfigs?: IPersonaCombinationSummary[];
}) => {
  const { control, getValues, register, setValue, trigger } = useFormContext<IPersonaLogicFormFields>();
  const mode = useWatch({ control, name: "persona_logic_mode" });
  const { errors } = useFormState({ control, name: "persona_combination" });
  const [modeError, setModeError] = useState<string | null>(null);

  register("persona_combination", { validate });

  const switchMode = (next: PersonaLogicMode) => {
    if (next === mode) return;
    setModeError(null);

    if (next === "ADVANCED") {
      setValue("persona_logic_mode", next);
      return;
    }

    const expression = getValues("persona_combination").trim();
    const parsed = expression ? validatePersonaCombination(expression, personas) : null;
    if (parsed && !parsed.rule) {
      setModeError("Fix the combination before switching to Basic.");
      return;
    }
    const basic = toBasicPersonaLogic(parsed?.rule ?? null);
    if (!basic) {
      setModeError("This combination uses nested logic that Basic can't show. Keep editing it here.");
      return;
    }
    setValue("basic_persona", basic, { shouldDirty: true });
    setValue("persona_combination", toBasicExpression(basic), { shouldDirty: true });
    setValue("persona_logic_mode", next);
    void trigger("persona_combination");
  };

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs font-semibold text-slate-700">Persona Logic</span>
        <SegmentedControl<PersonaLogicMode> options={PERSONA_LOGIC_MODES} value={mode} onChange={switchMode} />
      </div>
      {modeError && <p className="mb-2 text-[11px] text-amber-700">{modeError}</p>}

      {mode === "BASIC" ? (
        <>
          {isPersonaListLoading ? (
            <p className="py-4 text-center text-[11px] text-slate-500">Loading personas…</p>
          ) : (
            <BasicPersonaLogic personas={personas} />
          )}
          {errors.persona_combination?.message && (
            <p className="mt-1.5 text-[11px] text-rose-600">{errors.persona_combination.message}</p>
          )}
        </>
      ) : (
        <>
          <PersonaCombinationField
            personas={personas}
            isPersonaListLoading={isPersonaListLoading}
            validate={validate}
          />
          {existingConfigs && <CurrentCombinations configs={existingConfigs} />}
        </>
      )}
    </>
  );
};
